import { Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'

import { QuoteExplorerMode } from 'components/quote_explorer/QuoteExplorer'
import QuoteFlowButtonNav from 'components/quoting/QuoteFlowButtonNav'
import { getFullName } from 'components/reusable/form/StateDropdown'
import { Button } from 'slab'
import { useWindowLocation } from 'utils/windowLocation'

import AllowanceClass from './AllowanceClass'
import AllowanceInputBlock from './AllowanceInputBlock'
import { type Allowance, type Allowances, getDefaultAllowance } from './FlatAllowanceExplorer'

type Props = {
  mode: QuoteExplorerMode
  previousStepPath?: string
}

export type AllowanceWithIndex = Allowance & {
  index: number
}

function AllowanceConfiguration ({ mode, previousStepPath }: Props) {
  const { assign } = useWindowLocation()

  const [allowanceAddedAt, setAllowanceAddedAt] = useState<number>()
  const isExploreMode = mode === QuoteExplorerMode.explore

  const { control } = useFormContext<Allowances>()

  const { fields: allowanceFields, append: appendAllowance, remove: removeAllowance } = useFieldArray({
    control,
    name: 'allowances',
  })

  const defaultAllowance: AllowanceWithIndex = useMemo(() => {
    const defaultIndex = allowanceFields.findIndex(allowance => !allowance.class)
    return {
      ...allowanceFields[defaultIndex],
      index: defaultIndex,
    }
  }, [allowanceFields])

  const sortedClassAllowances: AllowanceWithIndex[] = useMemo(() => (
    allowanceFields
      .map((field, index) => ({
        ...field,
        index,
      }))
      .filter(allowance => allowance.class)
      .sort((a, b) => (getFullName(a.class?.location_id || '') || 'Z').localeCompare(getFullName(b.class?.location_id || '') || 'Z'))
  ), [allowanceFields, allowanceAddedAt])

  return (
    <div className='flex flex-col gap-6'>
      <AllowanceInputBlock
        label='Default allowance'
        description='Required; any employees not part of a defined class will be assigned this allowance structure'
        formIndex={defaultAllowance.index}
        readOnly={isExploreMode}
        loading={defaultAllowance.index === -1}
      />
      <div className='flex flex-col gap-2'>
        <div className='text-xs font-semibold text-gray-800 block mb-2'>
          Classes
        </div>
        {sortedClassAllowances.length > 0
          ? (
              sortedClassAllowances.map(allowance => (
                <div key={allowanceFields[allowance.index].id}>
                  <AllowanceClass
                    index={allowance.index}
                    defaultOpen={!allowance.class?.location_id}
                    mode={mode}
                    onAdd={() => setAllowanceAddedAt(Date.now())}
                    onRemove={() => removeAllowance(allowance.index)}
                  />
                </div>
              ))
            )
          : (
            <div className='flex items-center justify-center border border-dashed border-gray-400 rounded-lg p-3'>
              <div className='text-sm text-gray-500'>
                You currently have no classes. {isExploreMode ? 'All employees will be assigned the default allowance.' : 'Click "Add new class" to get started.'}
              </div>
            </div>
            )}
        {!isExploreMode && (
          <div className='pt-2'>
            <Button
              variant='tertiary'
              leftIcon={Plus}
              color='purple'
              onClick={() => {
                appendAllowance(
                  { class: { location_type: 'state' }, ...getDefaultAllowance() }
                )
              }}
            >
              Add new class
            </Button>
          </div>
        )}
        {previousStepPath && (
          <div className='pt-8 self-start'>
            <QuoteFlowButtonNav
              onBack={() => {
                assign(previousStepPath)
              }}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default AllowanceConfiguration
